import {
    AuthInfo,
    AuthType,
    CancellableWait,
    DefaultHttpRequestOptions,
    GetHexString,
    HttpRequestHeaders,
    IsChromeBrowser,
    IsChromeVersionAtLeast,
    Log,
    NetworkType,
    ObservableArray,
    PlatformDetails,
    RequestHttpOptions,
    Response,
    TracingManager,
    UtilsErrorCode,
    getNewGuid,
    getPlatformDetails,
    performHttpRequest
} from "./dependencies";
import { ScErrorCode } from "./gserrorcode";
import {
    AdAction,
    AdData,
    AdState,
    AdUpdate,
    AppLaunchMode,
    AppLevelProtocol,
    CLIENT_IDENTIFICATION,
    CLIENT_VERSION,
    ConnectionInfo,
    Protocol,
    SdrHdrMode,
    ServerInfo,
    SessionAdMediaFileData,
    SessionProgressState,
    SessionState,
    SessionUpdate,
    Usage
} from "./interfaces";
import {
    ActiveSessionsResult,
    SessionInfoResult,
    SessionControl,
    SessionStartResumeResult,
    SessionUpdateCallback,
    SessionParameters,
    InitializeParameters,
    Session
} from "./sessioncontrol";
import {
    GridServer_GameLaunch_Request,
    TelemetryHttpEvent,
    ScTelemetryHandler
} from "./gstelemetryhandler";
import { SessionControlSettings } from "./settings";
import { TracingComponent, TracingOperation } from "./tracing";
import { trace } from "@opentelemetry/api";
import { isCancelCode } from "./sessioncontrolutils";
import * as PM from "./schemas/pminterfaces.generated";

const LOGTAG = "sessioncontrolimpl";

/**
 * Contains parameters which helps decide the interval between GET session requests during session setup.
 * @see SessionControl.getPollingInterval
 */
export interface PollingOptions {
    /** Minimum interval between consecutive GET session requests */
    minInterval: number;
    /** Maximum interval between consecutive GET session requests. */
    maxInterval: number;
    /**
     * When sessions are in longer queue, the GET session request interval should be higher to reduce the load on the server.
     * From 0 to n, for every X number of queue position the interval needs to be incremented by step value.
     * Ex: For step = 2 and queueSizePerStep = 30.
     *  If session queue position is between 1-30, interval should be 1 * step which is 2.
     *  If session queue position is between 31-60, interval should be  2 * step which is 4.
     * Note: The resulting interval must be below maxInterval.
     */
    step: number;
    /** size of queue after which interval must be incremented by step. */
    queueSizePerStep: number;
    /**
     * When the server indicates session requires ads, library must delay sending the next GET session request until a minimum duration.
     * This helps in needlessly request server until it fetches ad details from third party service.
     * This value cannot override the minInterval and maxInterval values.
     */
    adsPollingIntervalMinMS: number;
}

/**
 * Creates NV-* headers and other general http headers to be sent to the server in all requests.
 * Various NV-* headers and its possible values are documented in https://confluence.nvidia.com/pages/viewpage.action?spaceKey=DCPC&title=LCARS+Documentation
 * @param httpHeaders - output parameters to which all the NV-* headers will be populated.
 * @param initParams - InitializeParameters passed to SessionControl which contains values for various NV-* headers.
 */
function addClientHeaders(httpHeaders: HttpRequestHeaders, initParams: InitializeParameters): void {
    httpHeaders["content-type"] = "application/json";
    if (initParams.deviceOs !== undefined) {
        httpHeaders["nv-device-os"] = initParams.deviceOs;
    }
    if (initParams.deviceType !== undefined) {
        httpHeaders["nv-device-type"] = initParams.deviceType;
    }
    if (initParams.deviceMake !== undefined) {
        httpHeaders["nv-device-make"] = initParams.deviceMake;
    }
    if (initParams.deviceModel !== undefined) {
        httpHeaders["nv-device-model"] = initParams.deviceModel;
    }
    if (initParams.clientType !== undefined) {
        httpHeaders["nv-client-type"] = initParams.clientType;
    }
    if (initParams.clientAppVersion !== undefined) {
        httpHeaders["nv-client-version"] = initParams.clientAppVersion;
    }
    if (initParams.clientStreamer !== undefined) {
        httpHeaders["nv-client-streamer"] = initParams.clientStreamer;
    }
    if (initParams.clientId !== undefined) {
        httpHeaders["nv-client-id"] = initParams.clientId;
    }
    if (initParams.deviceId !== undefined) {
        httpHeaders["x-device-id"] = initParams.deviceId;
    }
    if (initParams.browserType !== undefined) {
        httpHeaders["nv-browser-type"] = initParams.browserType;
    }

    if (initParams.clientHeaders) {
        for (const [key, value] of initParams.clientHeaders) {
            // Ignore deprecated header, GFNClientSDK needs to be updated to not send this header.
            if (key.toLowerCase() === "x-nv-client-identity") {
                continue;
            }
            httpHeaders[key.toLowerCase()] = value;
        }
    }
}

/**
 * Provides the resulting AdState for a given AdAction.
 * @param action - AdAction which would result in state transition.
 * @returns AdState - next state of the Ad.
 */
export function convertAdActionToState(action: AdAction): AdState {
    switch (action) {
        case AdAction.START:
            return AdState.STARTED;
        case AdAction.PAUSE:
            return AdState.PAUSED;
        case AdAction.RESUME:
            return AdState.RESUMED;
        case AdAction.FINISH:
            return AdState.FINISHED;
        case AdAction.CANCEL:
            return AdState.CANCELED;
        default:
            return AdState.UNKNOWN;
    }
}

/**
 * Checks whether the transition of Ad from one state to another is valid.
 * This is required to reject invalid AdUpdates from client. @see SessionControl.updateAdState
 * @param lastState - last state of the Ad.
 * @param newState - next state to be transitioned to based on clients AdUpdate
 * @returns true - if state transition is valid, else false.
 */
export function isValidAdStateTransition(lastState: AdState, newState: AdState): boolean {
    switch (newState) {
        case AdState.STARTED:
            return lastState == AdState.NOT_STARTED || lastState == AdState.UNKNOWN;
        case AdState.PAUSED:
            return lastState == AdState.STARTED || lastState == AdState.RESUMED;
        case AdState.RESUMED:
            return lastState == AdState.PAUSED;
        case AdState.FINISHED:
            return lastState == AdState.STARTED || lastState == AdState.RESUMED;
        case AdState.CANCELED:
            return lastState != AdState.FINISHED;
        default:
            return false;
    }
}

/**
 * Generates a code based on the 'statusCode' and 'unifiedErrorCode' of requestStatus data from server.
 * @param requestStatus - json 'requestStatus' received in http response from server.
 * @returns number - integer code which may or may not be defined in ScErrorCode.
 */
function getResultFromRequestStatus(pmRequestStatus: PM.RequestStatus): number {
    let result: number = ScErrorCode.SessionServerErrorBegin;
    if (pmRequestStatus?.statusCode !== undefined) {
        let statusCode = pmRequestStatus.statusCode;
        if (statusCode == PM.StatusCode.SUCCESS_STATUS) {
            result = ScErrorCode.Success;
        } else if (statusCode > 0 && statusCode < 0xff) {
            result = ScErrorCode.SessionServerErrorBegin + statusCode;
        }

        if (
            (result == ScErrorCode.ServerInternalError ||
                result == ScErrorCode.InvalidServiceResponse) &&
            pmRequestStatus.unifiedErrorCode
        ) {
            result = pmRequestStatus.unifiedErrorCode;
        }
    }
    return result;
}

/**
 * Converts the unified protocol status of the session to SessionState enum.
 * @param status - unified protocol session state received in http response.
 * @returns SessionState enum.
 */
function convertSessionStatus(status: PM.Status): SessionState {
    switch (status) {
        case PM.Status.INITIALIZING:
            return SessionState.INITIALIZING;
        case PM.Status.READY_FOR_CONNECTION:
            return SessionState.READY_FOR_CONNECTION;
        case PM.Status.PLAYING:
            return SessionState.STREAMING;
        case PM.Status.PAUSED_UNINTENTIONAL:
        case PM.Status.PAUSED_INTENTIONAL:
            return SessionState.PAUSED;
        case PM.Status.RESUMING:
            return SessionState.RESUMING;
        case PM.Status.FINISHED:
            return SessionState.FINISHED;
        default:
            return SessionState.UNKNOWN;
    }
}

/**
 * Informs if the err was generated due to failure in parsing the json data.
 * @param err - Error returned by browser or throw from internal code.
 * @returns true - if err is parsing failure else false.
 */
function isParseError(err: any): boolean {
    let result = false;
    if (
        err instanceof SyntaxError ||
        (err instanceof TypeError && err.message && err.message.includes("Cannot read property"))
    ) {
        result = true;
        Log.e("{1daeef1}", "{d8979bb}", err.name, err.message);
    }
    return result;
}

/**
 * Utility function which converts the UtilsErrorCode to SessionControl error code.
 * @param code - UtilsErrorCode
 * @returns ScErrorCode for the given UtilsErrorCode
 */
function convertUtilsErrorCodeToScErrorCode(code: number): ScErrorCode {
    let scCode = ScErrorCode.DOMExceptionInSessionControl; // https://developer.mozilla.org/en-US/docs/Web/API/DOMException#error_names
    if (code === UtilsErrorCode.NoNetwork) {
        scCode = ScErrorCode.NoInternetDuringSessionSetup;
    } else if (code === UtilsErrorCode.NetworkError) {
        scCode = ScErrorCode.NetworkError;
    } else if (code === UtilsErrorCode.AuthTokenNotUpdated) {
        scCode = ScErrorCode.AuthTokenNotUpdated;
    } else if (code === UtilsErrorCode.ResponseParseFailure) {
        scCode = ScErrorCode.ResponseParseFailure;
    } else if (code === ScErrorCode.GetActiveSessionServerError) {
        // We throw this gsError from getSessions and hence need this
        scCode = ScErrorCode.GetActiveSessionServerError;
    } else if (code == UtilsErrorCode.UserAborted) {
        scCode = ScErrorCode.RequestCancelled;
    }
    return scCode;
}

/**
 * Utility function which provides string form of few generic error codes which can used for logging.
 * @see SessionControl.performSessionRequest
 * @param code - Error generated during server request, could be ScErrorCode or unified error code returned by server.
 * @returns a string which can be logged.
 */
export function getErrorCodeDescription(code: number): string {
    switch (code) {
        case ScErrorCode.InvalidOperation:
            return "invalid operation";
        case ScErrorCode.Success:
            return "success";
        case ScErrorCode.ResponseParseFailure:
        case ScErrorCode.InvalidServerResponse:
            return "Invalid server response";
        case ScErrorCode.DOMExceptionInSessionControl:
            return "Exception";
        case ScErrorCode.RequestCancelled:
        case ScErrorCode.SessionSetupCancelled:
        case ScErrorCode.SessionSetupCancelledDuringQueuing:
            return "cancelled";
        case ScErrorCode.NoInternetDuringSessionSetup:
            return "no network";
        case ScErrorCode.NetworkError:
            return "network error";
        default:
            return "server error";
    }
}

/**
 * Collection of the output data from @see SessionControlImpl.performSessionRequest
 */
export interface ServerResponse {
    /** Result code for the current http request. */
    result: ScErrorCode;
    /**
     * Session object generated from server response.
     * If http requests fails due to network error then this object will not be populated.
     */
    session?: Session;
    /** Id of the server instance which responded to the http request. */
    serverId?: string;
    /** Active session associated with the user, populated for SessionRequestType.GET_ACTIVE_SESSIONS and in SessionRequestType.CREATE_SESSION(optional) */
    activeSessions?: Session[];
    /** Actual json response from server. */
    pmResponse?: PmResponse;
}

/**
 * Identifies the type of http request to @see SessionControl.performSessionRequest.
 */
export enum SessionRequestType {
    CREATE_SESSION,
    RESUME_SESSION,
    DELETE_SESSION,
    GET_SESSIONINFO,
    AD_UPDATE,
    GET_ACTIVE_SESSIONS
}

// todo: since all the code related to session setup is now contained in a class, processAdUpdate can be restructured to remove these handlers.
type ServerResponseHandler = (response: ServerResponse) => void;
type ExceptionHandler = (error: any) => void;
type RequestAbortHandler = () => void;
type TimeoutHandler = () => void;

/**
 * todo SessionSetupController:
 * Below functions of SessionControlImpl can be moved to a class called SessionSetupController which wraps everything that needs to happen during Session setup process.
 *  startOrResumeSession,
 *  performSessionSetupPollingRequest,
 *  processSessionSetupException, resolveStartResumeSession, processSessionSetupServerResponse,
 *  getPollingInterval,
 *  updateAdState implementation,
 *  getSessionUpdate
 * Some of the members of SessionControlImpl like pollingOptions, adUpdateArray, adsStateInfo should also be moved to SessionSetupController.
 * While implementing SessionSetupController, some functions like extractSessionInformation, extractSessionList, performSessionRequest should all be made accessible to SessionSetupController.
 * Right now majority of the SessionControlImpl properties are combined in SessionSetupData struct and letting the derived GridServer class utilize member functions of SessionControl for polling.
 * It would be better to take up moving of functionalities to SessionSetupController after GridServer class is deleted,
 * otherwise will require some WARs in design which again needs to be cleaned up.
 */

/** Promise return type of SessionControl.startSession/resumeSession */
type SessionStartResumeResolve = (
    value: SessionStartResumeResult | PromiseLike<SessionStartResumeResult>
) => void;

/**
 * Collection of the parameters required in session setup process.
 * Temporary structure required until SessionSetupController implementation.
 */
interface SessionSetupData {
    /** sessionSession/resumeSession returned promise resolve function. */
    resolve: SessionStartResumeResolve;
    /** Promise rejection function. */
    reject: any;
    /** Telemetry events for the session setup process. Emitted before resolving the promise. */
    launchTelemetryEvent: GridServer_GameLaunch_Request;
    /** Current session Id. Will be updated after POST session response in startSession case.*/
    sessionId?: string;
    /** SubSessionId for the current startSession/resumeSession. */
    subSessionId: string;
    /** Cached session details to be provided to client in case of network failures. */
    lastSessionFromServer?: Session;
    /** Callback to deliver the SessionUpdate data to clients. */
    sessionUpdateCallback: SessionUpdateCallback;
    /**
     * Last queue position sent to client in SessionUpdate.
     * Required to prevent sending queue position higher than the previously provided queue position to client.
     * This is a WAR for UI progress bar going back and forth issue. */
    lastQueuePosition: number;
    /**
     * Indicates if the session was put in queue on server.
     * Will be used to distinguish result code between SessionSetupCancelled and SessionSetupCancelledDuringQueuing.
     */
    isSessionInQueue: boolean;
    /** Abort controller for terminating the setup process. */
    cancelController: AbortController;
    /** Start time of the session setup process, eventually the SessionSetupController class will set this in its constructor. */
    startTime: number;
}

/** A union of possible responses from the PM */
declare interface PmResponse {
    requestStatus?: PM.RequestStatus;
    sessions?: PM.Session[];
    session?: PM.Session;
    otherUserSessions?: PM.Session[];
}

const BROWSER_PLATFORM_NAME = "browser";
const SESSION_URL_PATH = "/v2/session";

/**
 * Implementation class of @see SessionControl interfaces.
 * This class is not exported outside of this library.
 */
export class SessionControlImpl implements SessionControl {
    // todo: Majority of the members here are protected as derived GridServer class uses them.
    // in final CL some of the members will be changed to private and when GridServer is deleted majority of these will be made private
    protected sessionControlServerMap: Map<string, ServerInfo> = new Map();
    protected httpRequestOptions: RequestHttpOptions = { ...DefaultHttpRequestOptions };
    protected authInfo: AuthInfo = { type: AuthType.JWT_GFN };
    protected tracingAttributes: Map<string, string> = new Map();
    protected protocol: string = "https://";
    protected platformDetails?: PlatformDetails;
    // todo: members required during session setup, to be moved to SessionSetupController.
    protected sessionSetupData?: SessionSetupData;
    protected adUpdateArray: ObservableArray<AdUpdate> = new ObservableArray<AdUpdate>();
    protected adsStateInfo: Map<string, { serverAdState: AdState; lastClientAdState: AdState }> =
        new Map();
    protected pollingOptions: PollingOptions;
    protected serverAddress: string = "";
    private deviceId: string = "";
    private clientPlatformName: string = "";

    constructor() {
        // Native clients can have separate handling of connectionTimeout and ReceiveTimeout. However fetch API doesn't provide this differentiation.
        // Combine these two values from remote configuration to pass to fetch API.
        let httpTimeout = 0; // in seconds
        httpTimeout +=
            SessionControlSettings.commonConfig.pmCommunication?.httpConnectionTimeout ?? 0;
        httpTimeout +=
            SessionControlSettings.commonConfig.pmCommunication?.httpDataReceiveTimeout ?? 0;
        this.httpRequestOptions.timeout = httpTimeout ? httpTimeout * 1000 : 13000; // in milliseconds

        this.httpRequestOptions.retryCount =
            SessionControlSettings.commonConfig.pmCommunication?.httpRetryCount ?? 3;
        this.httpRequestOptions.backOffDelay =
            SessionControlSettings.commonConfig.pmCommunication?.httpBackOffDelay ?? 500;

        this.pollingOptions = {
            minInterval:
                SessionControlSettings.commonConfig.pmCommunication?.pollingIntervalMin ?? 1000,
            maxInterval:
                SessionControlSettings.commonConfig.pmCommunication?.pollingIntervalMax ?? 10000,
            step: SessionControlSettings.commonConfig.pmCommunication?.pollingIntervalStep ?? 1000,
            queueSizePerStep:
                SessionControlSettings.commonConfig.pmCommunication?.pollingQueueSizePerStep ?? 50,
            adsPollingIntervalMinMS:
                SessionControlSettings.commonConfig.pmCommunication?.adsPollingIntervalMinMS ?? 3000
        };
    }

    /**
     * Initializes the SessionControl class.
     * This is a temporary function added to support the derived class GridServer initialization which needs to wait for client calling init for internal configuration.
     * todo: Once GridServer is deleted, move all the content of this to function to constructor.
     * @param initializeParams
     * @returns true if successfully initialized.
     */
    public init(initializeParams: InitializeParameters): boolean {
        // Prepare all the NV-* client headers sent to server in every http request.
        addClientHeaders(this.httpRequestOptions.headers, initializeParams);
        // set all common tracing attributes.
        // todo: we should consider extending TracingManager to accept these headers and let it use them whenever a new span is created.
        // or we could create a SessionControlTracingManager and move this and other tracing related functions to it.
        this.tracingAttributes.set("component", TracingComponent.name);
        this.tracingAttributes.set("component.version", TracingComponent.version);
        this.tracingAttributes.set("client.name", CLIENT_IDENTIFICATION);
        this.tracingAttributes.set("client.version", CLIENT_VERSION);
        this.tracingAttributes.set("client.os", initializeParams.deviceOs ?? "");
        this.tracingAttributes.set("client.os.version", initializeParams.deviceOsVer ?? "");

        // Some of the initialize parameters like deviceId, platform name are sent in start/resume request to server.
        // Need to cache this until sending those data is moved to client headers or deprecated.
        this.serverAddress = initializeParams.serverAddress;
        this.clientPlatformName = initializeParams.clientPlatformName;
        this.deviceId = initializeParams.deviceId;

        // MockPM mode only supports http version for now. If there is a need then we can add secure option.
        // Note: we are not parsing for mockpm:// in the SessionParameters.serverAddress as we don't expect that to be populated in this mode.
        if (
            this.serverAddress.startsWith("mockpm://") ||
            this.serverAddress.toLocaleLowerCase() === "mockpm.com"
        ) {
            this.serverAddress = this.serverAddress.replace("mockpm://", "");
            this.protocol = "http://";
        }

        // GridServer interface accepts platformDetails in constructor, platform details are required in this library only for determining keepAlive option for fetch request.
        // getting platformDetails should not be an expensive operation and we can obtain this internally without need for clients to pass this information.
        if (!this.platformDetails && this.clientPlatformName === BROWSER_PLATFORM_NAME) {
            getPlatformDetails().then((platform: PlatformDetails) => {
                this.platformDetails = platform;
            });
        }

        // we don't have failure cases yet, always return true.
        return true;
    }

    // @see SessionControl.setAuthInfo
    public setAuthInfo(authInfo: AuthInfo) {
        this.authInfo = authInfo;
    }

    // @see SessionControl.setNetworkType
    public setNetworkType(network: NetworkType) {
        ScTelemetryHandler.setNetworkType(network);
    }

    // @see SessionControl.startSession
    public startSession(
        startParameters: SessionParameters,
        cancelController: AbortController,
        updateCallback: SessionUpdateCallback
    ): Promise<SessionStartResumeResult> {
        const request = () => {
            this.addTracingAttributesToSpan();
            return this.startOrResumeSession(startParameters, cancelController, updateCallback);
        };
        return TracingManager.getInstance().createSpan<SessionStartResumeResult>(
            TracingOperation.StartSession,
            request
        );
    }

    // @see SessionControl.resumeSession
    public resumeSession(
        sessionId: string,
        resumeParameters: SessionParameters,
        cancelController: AbortController,
        callback: SessionUpdateCallback
    ): Promise<SessionStartResumeResult> {
        const request = () => {
            this.addTracingAttributesToSpan();
            return this.startOrResumeSession(
                resumeParameters,
                cancelController,
                callback,
                sessionId
            );
        };
        return TracingManager.getInstance().createSpan<SessionStartResumeResult>(
            TracingOperation.ResumeSession,
            request
        );
    }

    // @see SessionControl.deleteSession
    public deleteSession(
        sessionId: string,
        cancelController?: AbortController
    ): Promise<ScErrorCode> {
        const impl = () => {
            this.addTracingAttributesToSpan();

            if (!this.isAuthInfoAvailable()) {
                Log.e("{1daeef1}", "{4fed44a}");
                const result = ScErrorCode.InvalidOperation;
                this.addResultToSpan(result);
                return Promise.resolve(result);
            }

            const url = this.getUrlForSession(sessionId);
            return this.performSessionRequest(
                SessionRequestType.DELETE_SESSION,
                url,
                "",
                cancelController,
                sessionId
            )
                .then((response: ServerResponse) => {
                    if (response.result == ScErrorCode.Success) {
                        // There will no more requests to this session, remove this session session control map.
                        this.sessionControlServerMap.delete(sessionId);
                        Log.d("{1daeef1}", "{6206c13}", sessionId);
                    } else {
                        Log.e("{1daeef1}", "{4de47e3}", sessionId, GetHexString(response.result));
                    }
                    this.addResultToSpan(response.result);
                    return response.result;
                })
                .catch((error: any) => {
                    this.processException(error, "deleteSession");
                    const result = ScErrorCode.DOMExceptionInSessionControl;
                    this.addResultToSpan(result);
                    return result;
                });
        };

        return TracingManager.getInstance().createSpan<ScErrorCode>(
            TracingOperation.DeleteSession,
            impl
        );
    }

    // @see SessionControl.getActiveSessions
    public getActiveSessions(
        cancelController: AbortController | undefined
    ): Promise<ActiveSessionsResult> {
        const impl = () => {
            this.addTracingAttributesToSpan();

            if (!this.isAuthInfoAvailable()) {
                Log.e("{1daeef1}", "{dc27839}");
                const result = ScErrorCode.InvalidOperation;
                this.addResultToSpan(result);
                return Promise.resolve({ code: result });
            }

            const url = this.protocol + this.serverAddress + SESSION_URL_PATH;
            return this.performSessionRequest(
                SessionRequestType.GET_ACTIVE_SESSIONS,
                url,
                "",
                cancelController
            )
                .then((response: ServerResponse) => {
                    const result: ActiveSessionsResult = {
                        code: response.result,
                        activeSessions: response.activeSessions,
                        zoneName: response.serverId
                    };
                    this.addResultToSpan(result.code);

                    if (result.code !== ScErrorCode.Success) {
                        Log.e("{1daeef1}", "{9d2bb75}", GetHexString(result.code));
                    } else {
                        Log.d("{1daeef1}", "{081857f}");
                    }
                    return result;
                })
                .catch((error: any) => {
                    this.processException(error, "getActiveSessions");
                    const result: ActiveSessionsResult = {
                        code: ScErrorCode.DOMExceptionInSessionControl
                    };
                    this.addResultToSpan(result.code);
                    return result;
                });
        };

        return TracingManager.getInstance().createSpan<ActiveSessionsResult>(
            TracingOperation.GetSessionList,
            impl
        );
    }

    // @see SessionControl.getSessionInfo
    public getSessionInfo(
        sessionId: string,
        cancelController: AbortController | undefined
    ): Promise<SessionInfoResult> {
        const impl = () => {
            const url = this.getUrlForSession(sessionId);
            return this.performSessionRequest(
                SessionRequestType.GET_SESSIONINFO,
                url,
                "",
                cancelController,
                sessionId
            )
                .then((response: ServerResponse) => {
                    const result: SessionInfoResult = {
                        code: response.result,
                        session: response.session,
                        zoneName: response.serverId
                    };

                    if (result.code !== ScErrorCode.Success) {
                        Log.e("{1daeef1}", "{021d0f4}", GetHexString(result.code));
                    } else {
                        Log.d("{1daeef1}", "{fa53c7c}");
                    }
                    return result;
                })
                .catch((error: any) => {
                    this.processException(error, "getSessionInfo");
                    const result: SessionInfoResult = {
                        code: ScErrorCode.DOMExceptionInSessionControl
                    };
                    this.addResultToSpan(result.code);
                    return result;
                });
        };
        return TracingManager.getInstance().createSpan<SessionInfoResult>(
            TracingOperation.GetSession,
            impl
        );
    }

    /**
     * Indicates if execution of startSession/resumeSession is in progress.
     * note: GridServer overrides and determines this based on this.getSessionRequestController.
     * @returns boolean
     */
    protected isSessionSetupInProgress(): boolean {
        return !!this.sessionSetupData;
    }

    /**
     * Adds common attributes to the active tracing span.
     * Must be called whenever a new span is created.
     * todo: evaluate if this can be moved to TracingManager.
     */
    protected addTracingAttributesToSpan(): void {
        const span = trace.getActiveSpan();
        if (span) {
            for (const [key, value] of this.tracingAttributes) {
                span.setAttribute(key, value);
            }
        }
    }

    /**
     * Updates the span with the result of the operation.
     * @param code - Returned by SessionControl APIs
     * @param description - optional description to be set in span.
     */
    protected addResultToSpan(code: ScErrorCode, description?: string): void {
        const span = trace.getActiveSpan();
        if (!span) {
            return;
        }

        if (isCancelCode(code)) {
            span.setAttribute("error", false);
            span.setAttribute("error.description", getErrorCodeDescription(code));
        } else if (code == ScErrorCode.Success) {
            span.setAttribute("error", false);
        } else {
            span.setAttribute("error", true);
            span.setAttribute("error.description", description ?? getErrorCodeDescription(code));
        }
    }

    /**
     * Updates the span with session properties.
     */
    protected addSessionInfoToSpan(pmSession: PM.Session): void {
        const span = trace.getActiveSpan();
        span?.setAttribute("session.status", pmSession.status ?? "");
    }

    /**
     * Updates the span with RequestStatus details of http response from server.
     * @param requestStatus - RequestStatus object in the response from server.
     */
    protected addRequestStatusToSpan(requestStatus: PM.RequestStatus): void {
        const span = trace.getActiveSpan();
        if (!span) {
            return;
        }

        const requestStatusAttributes: Map<string, string> = new Map([
            ["host.dc", requestStatus.serverId ?? ""],
            ["request.id", requestStatus.requestId ?? ""],
            ["request.status", requestStatus.statusDescription ?? ""]
        ]);
        for (const [key, value] of requestStatusAttributes) {
            span.setAttribute(key, value);
        }
    }

    /**
     * Creates Session object from the json response from server
     * @param pmSession - the "session" objected contained in the http response from the server.
     * @returns Session
     */
    private extractSessionInformation(pmSession: PM.Session): Session {
        let session: Session = {
            sessionId: pmSession.sessionId,
            subSessionId: "",
            appId: 0,
            appLaunchMode: AppLaunchMode.Default,
            state: convertSessionStatus(pmSession.status),
            zoneAddress: "",
            zoneName: "",
            deviceId: "",
            gpuType: "",
            monitorSettings: [],
            connectionInfo: [],
            finalizedStreamingFeatures: pmSession.finalizedStreamingFeatures
        };

        session.appId = pmSession.sessionRequestData ? pmSession.sessionRequestData.appId : 0;

        if (pmSession.connectionInfo) {
            const connectionInfoList: ConnectionInfo[] = pmSession.connectionInfo;

            // For Browser clients PM is not returning right port information.
            if (pmSession.sessionRequestData?.clientPlatformName === BROWSER_PLATFORM_NAME) {
                let videoPortFound: boolean = false;
                // In web client- GeForce Now service, we need only two ports, but PM is returning a lot of useless ports and incorrect information for signaling.
                for (const connectionInfo of connectionInfoList) {
                    if (connectionInfo.usage === Usage.VIDEO) {
                        videoPortFound = true;
                        session.connectionInfo.push(connectionInfo);

                        const signalingInfo: ConnectionInfo = {
                            ip: "",
                            port: 49100,
                            appLevelProtocol: AppLevelProtocol.HTTPS,
                            protocol: Protocol.TCP,
                            usage: Usage.SIGNALING,
                            resourcePath: ""
                        };
                        let addressParts = connectionInfo.ip.split(".");
                        signalingInfo.ip =
                            addressParts[0] +
                            "-" +
                            addressParts[1] +
                            "-" +
                            addressParts[2] +
                            "-" +
                            addressParts[3];
                        let domainUrlIndex = pmSession.sessionControlInfo!.ip.indexOf(".");
                        signalingInfo.ip +=
                            pmSession.sessionControlInfo!.ip.substring(domainUrlIndex);
                        signalingInfo.resourcePath = signalingInfo.ip;
                        Log.d("{1daeef1}", "{cf7d7ea}", signalingInfo.ip);

                        session.connectionInfo.push(signalingInfo);
                        break;
                    }
                }

                if (!videoPortFound) {
                    // MockPM provides only signaling port and doesn't need override of ICE connection with video port.
                    // We can also use this flow for eventual transition to BUNDLE port streaming.
                    // Right now PM sends both BUNDLE and VIDEO port, eventually we should get only one valid signaling and BUNDLE connection info.
                    session.connectionInfo = connectionInfoList;
                }
            } else {
                session.connectionInfo = connectionInfoList;
            }
        }

        if (pmSession.monitorSettings) {
            session.monitorSettings = pmSession.monitorSettings;
        }

        if (pmSession.gpuType) {
            session.gpuType = pmSession.gpuType;
        }

        switch (pmSession.sessionRequestData?.appLaunchMode) {
            case PM.AppLaunchMode.TOUCH_FRIENDLY:
                session.appLaunchMode = AppLaunchMode.TouchFriendly;
                break;
            case PM.AppLaunchMode.GAMEPAD_FRIENDLY:
                session.appLaunchMode = AppLaunchMode.GamepadFriendly;
                break;
        }

        if (pmSession.sessionControlInfo) {
            const newServerIp = pmSession.sessionControlInfo.ip;
            const newServerPort = pmSession.sessionControlInfo.port;
            if (newServerIp && newServerPort) {
                if (this.sessionControlServerMap.has(session.sessionId)) {
                    const currentServer = this.sessionControlServerMap.get(session.sessionId);

                    if (
                        currentServer?.server !== newServerIp ||
                        currentServer?.port !== newServerPort
                    ) {
                        Log.i("{1daeef1}", "{f09f5be}", session.sessionId, currentServer?.server, currentServer?.port, newServerIp, newServerPort);
                    }
                }

                this.sessionControlServerMap.set(session.sessionId, {
                    server: newServerIp,
                    port: newServerPort
                });
            }

            session.zoneAddress = newServerIp;
            session.zoneName = newServerIp.split(".")[0].toUpperCase();
        }

        return session;
    }

    /**
     * Converts the array of unified protocol session object to Session array.
     * @param pmSessions - the array of unified protocol session objects.
     * @returns Session[]
     */
    private extractSessionList(pmSessions: PM.Session[]): Session[] {
        return pmSessions.map(session => this.extractSessionInformation(session));
    }

    /**
     * This function is executed during session setup/resume process if the server has provided ads for the session.
     * Clients are expected to play the ads received from server and update the ad state to library. @see SessionControl.updateAdState
     * Since requests to server are always sent sequentially, the updateAdState function just queues the AdUpdate.
     * This function sends the queued AdUpdates to server.
     * If there are no AdUpdates to be sent to server then either wait for timeout or for new AdUpdate from the client or abort signal from client.
     * @param sessionId - Id of the session thats being setup.
     * @param setupController - AbortController to abort the request to server or abort the wait for timeout.
     * @param onServerResponse - Handler to be executed once response is received from server.
     * @param onException - Handler to be executed during exception.
     * @param onAbort - Handler to be executed when clients abort the request via setupController parameter.
     * @param onTimeout - Handler to be executed when clients doesn't provide any AdUpdate for a long time.
     */
    protected processAdUpdates(
        sessionId: string,
        setupController: AbortController,
        onServerResponse: ServerResponseHandler,
        onException: ExceptionHandler,
        onAbort: RequestAbortHandler,
        onTimeout: TimeoutHandler
    ): void {
        const sendAdUpdateRequest = () => {
            this.addTracingAttributesToSpan();

            let url = this.getUrlForSession(sessionId);
            let requestData: PM.SessionModify = {
                action: PM.Action.AD_UPDATE,
                adUpdates: Array.from(this.adUpdateArray)
            };

            Log.d("{1daeef1}", "{dbca4b1}", JSON.stringify(requestData));
            this.adUpdateArray.clear();

            this.performSessionRequest(
                SessionRequestType.AD_UPDATE,
                url,
                JSON.stringify(requestData),
                setupController,
                sessionId
            )
                .then((serverResponse: ServerResponse) => {
                    onServerResponse(serverResponse);
                })
                .catch(err => onException(err));
        };

        // Note:
        // 1. The below flush could have been done inside the updateAdState function as well.
        // Right now we are blindly flushing PAUSE/RESUME combo, eventually this might change,
        // flush can be based on timestamp difference (+ad duration), duration from previous https request, number of retries required for the previous http request etc.
        // 2. We only flush PAUSE/RESUME in combination. If we have delivered a PAUSE action to PM then RESUME must be sent to PM.
        // otherwise PM might reject the requests because of wrong state transitions.
        let i = this.adUpdateArray.length - 1;
        let pairRemovedCount = 0;
        while (i >= 1) {
            if (
                this.adUpdateArray[i].adAction === AdAction.RESUME &&
                this.adUpdateArray[i - 1].adAction === AdAction.PAUSE
            ) {
                this.adUpdateArray.splice(i - 1, 2);
                pairRemovedCount++;
                i -= 2;
            } else if (this.adUpdateArray[i - 1].adAction === AdAction.RESUME) {
                // last but one element is RESUME, decrement by 1 to check if the previous element to it is PAUSE.
                i--;
            } else {
                // last but one element is not RESUME, cannot make a pair with it, decrement index by 2 to find next pair.
                i -= 2;
            }
        }

        if (pairRemovedCount) {
            Log.d("{1daeef1}", "{98a2816}", pairRemovedCount);
        }

        let sendAdUpdates: boolean = false;
        let waitTime: number = 10 * 60 * 1000; // 10 min
        const adUpdateCount = this.adUpdateArray.length;
        // If the last update is Finish and we have more ads then good to wait for some more time to batch the Ads start of next Ad.
        // Or if we have paused then good to wait for some more time to see if user resumes quickly.
        // Clients pause/resume based on webpage visibility. The wait time of 2 seconds is randomly chosen value now.
        // This value might change eventually and it could be based on the duration of ads, as PlayerWON service tracks when they are getting the requests.
        if (adUpdateCount) {
            if (
                (this.adUpdateArray[adUpdateCount - 1].adAction === AdAction.FINISH &&
                    this.adsStateInfo.size > 1) ||
                this.adUpdateArray[adUpdateCount - 1].adAction === AdAction.PAUSE
            ) {
                waitTime = 2000; // do a shorter wait and then send the request to PM.
            } else {
                sendAdUpdates = true;
            }
        }

        if (sendAdUpdates) {
            TracingManager.getInstance().createSpan(TracingOperation.AdUpdate, sendAdUpdateRequest);
        } else {
            // Wait for either
            // 1. "waitTime" to send the AdUpdate.
            // 2. "abortController" is signalled.
            // 3. Until next AdUpdate is pushed

            // In order to wait for client invoking AdUpdate, we set the resolve function of the promise as callback of push in adUpdateArray.
            // A new push will trigger the callback(resolve) to get executed which would resolve the below Promise.race
            const nextAdUpdatePromise = new Promise<void>((resolve, reject) => {
                // Make sure to remove this callback as all reference to resolve/reject and promise need to be removed for GC to free the promise.
                // Since we invoke processAdUpdate again as part of promise resolve, cleanup before instead of waiting for finally.
                this.adUpdateArray.setCallback("push", resolve);
            });

            const wait = new CancellableWait(waitTime, setupController.signal);

            Promise.race([wait.getPromise(), nextAdUpdatePromise])
                .then(result => {
                    // clearing the callback from adUpdateArray will remove the reference of resolve which will let Garbage collector cleanup the nextAdUpdatePromise
                    this.adUpdateArray.setCallback("push", undefined);
                    wait.cancel(); // in case nextAdUpdatePromise was resolved this will clear the timers and abort handler of signal.
                    if (this.adUpdateArray.length) {
                        if (this.adUpdateArray.length == adUpdateCount) {
                            // timeout, no new ad update.
                            TracingManager.getInstance().createSpan(
                                TracingOperation.AdUpdate,
                                sendAdUpdateRequest
                            );
                        } else {
                            this.processAdUpdates(
                                sessionId,
                                setupController,
                                onServerResponse,
                                onException,
                                onAbort,
                                onTimeout
                            );
                        }
                    } else {
                        Log.w("{1daeef1}", "{5e1dfd0}");
                        onTimeout();
                    }
                })
                .catch(err => {
                    // remove callbacks as abort on signal can result in this block execution.
                    this.adUpdateArray.setCallback("push", undefined);
                    // wait Promise gets rejected if the signal is aborted, we can also hit this block if the execution in the above 'then' block throws an exception.
                    if (wait.isAborted()) {
                        Log.i("{1daeef1}", "{d7114d7}");
                        onAbort();
                    } else {
                        onException(err);
                    }
                });
        }
    }

    public updateAdState(adUpdate: AdUpdate): ScErrorCode {
        if (!this.isSessionSetupInProgress()) {
            Log.e("{1daeef1}", "{20d9281}");
            return ScErrorCode.InvalidOperation;
        }

        const adStateInfo = this.adsStateInfo.get(adUpdate.adId);
        if (!adStateInfo) {
            Log.e("{1daeef1}", "{74daa9c}", adUpdate.adId);
            return ScErrorCode.InvalidAdStateTransition;
        }

        // Our module is designed to keep accepting AdAction from the client. We might not be able to send the updates to PM and get the latest AdState from PM
        // We save the last state from client and check if the Client is providing the right AdState transitions.
        const nextAdState = convertAdActionToState(adUpdate.adAction);
        if (!isValidAdStateTransition(adStateInfo.lastClientAdState, nextAdState)) {
            Log.e("{1daeef1}", "{09d8184}", adUpdate.adAction, adUpdate.adId, adStateInfo.lastClientAdState);
            return ScErrorCode.InvalidAdStateTransition;
        }

        if (this.adUpdateArray.length) {
            const previousAdId = this.adUpdateArray[this.adUpdateArray.length - 1].adId;
            if (previousAdId !== adUpdate.adId) {
                const previousAdsAction =
                    this.adUpdateArray[this.adUpdateArray.length - 1].adAction;
                if (previousAdsAction != AdAction.FINISH && previousAdsAction != AdAction.CANCEL) {
                    // we are still expecting ad update for previous Ad, client jumped to next Ad.
                    Log.e("{1daeef1}", "{c161587}", previousAdId);
                    return ScErrorCode.InvalidAdStateTransition;
                }
            }
        }

        if (!adUpdate.clientTimestamp) {
            // clientTimestamp should be unix time which is number of seconds since 00:00:00 Jan 1st 1970 UTC
            // Date.now() returns milliseconds precision, convert to seconds.
            adUpdate.clientTimestamp = Math.round(Date.now() / 1000);
        }

        // Pushing an element to adUpdateArray will resolve the promise.race which is waiting on the overridden push function.
        // the session setup flow will process this AdUpdate and determine the next steps.
        // Note: we do not attempt to directly make an https request to PM here as an AdUpdate requests might already in progress.
        // All requests to PM must be done sequentially hence we just push here and let the normal session setup flow process this request.
        this.adUpdateArray.push(adUpdate);
        adStateInfo.lastClientAdState = nextAdState;
        this.adsStateInfo.set(adUpdate.adId, adStateInfo);

        return ScErrorCode.Success;
    }

    /**
     * Generates a url for session requests. For new/resume session @see createSessionRequestURL to add additional query parameters.
     * @param sessionId - if set (all request except GET_ACTIVE_SESSIONS and CREATE_SESSION), will add the sessionId to URL
     * @param serverAddress - optional, will be used in URL creation if the session resource is not yet allocated in any zone.
     * @returns URL
     */
    protected getUrlForSession(sessionId: string): string {
        let sessionUrl = this.protocol;
        if (sessionId && this.sessionControlServerMap.has(sessionId)) {
            let sessionControlInfo = this.sessionControlServerMap.get(sessionId);
            if (sessionControlInfo !== undefined) {
                sessionUrl += sessionControlInfo.server;
                if (sessionControlInfo.port != 0) {
                    sessionUrl += ":" + sessionControlInfo.port;
                }
            }
        } else {
            sessionUrl += this.serverAddress;
        }
        sessionUrl += SESSION_URL_PATH + "/" + sessionId;
        return sessionUrl;
    }

    /**
     * Utility function to create the URL for new session or resume request.
     * The resume requests should always be sent to the zone where session is established.
     * @param startParams - SessionParameters which contains keyboardLayout, locale which are populated in query params of url.
     * @param sessionId - if set will create resume request URL.
     * @returns url
     */
    protected createSessionRequestURL(
        startResumeParams: SessionParameters,
        sessionId?: string | undefined
    ): string {
        let sessionUrl = "";
        if (sessionId) {
            sessionUrl = this.getUrlForSession(sessionId);
        } else {
            const server = startResumeParams.serverAddress ?? this.serverAddress;
            sessionUrl = this.protocol + server + SESSION_URL_PATH;
        }
        sessionUrl += "?keyboardLayout=" + (startResumeParams.keyboardLayout ?? "en_US");
        sessionUrl += "&languageCode=" + (startResumeParams.clientLocale ?? "en_US");
        return sessionUrl;
    }

    /**
     * Creates the SessionRequestData JSON object which should be sent to server in new session and resume requests.
     * @param sessionParameters - contains some of the values to be sent to server.
     * @param resume - if set, the JSON data generated will adhere to the resume request protocol.
     * @param subSessionId - Id generated by SessionControl in startSession/resumeSession interfaces.
     * @returns string - stringified JSON object to be sent to server.
     */
    protected getSessionRequestData(
        sessionParameters: SessionParameters,
        resume: boolean,
        subSessionId: string
    ): string {
        let appLaunchMode = 1; // Default
        switch (sessionParameters.appLaunchMode) {
            case AppLaunchMode.TouchFriendly:
                Log.d("{1daeef1}", "{6d5cf87}");
                appLaunchMode = 3;
                break;
            case AppLaunchMode.GamepadFriendly:
                appLaunchMode = 2;
                break;
            default:
                appLaunchMode = 1;
        }

        let sdrHdrMode = sessionParameters.monitorSettings?.[0]?.sdrHdrMode ?? SdrHdrMode.SDR;

        if (SessionControlSettings.hdr) {
            sdrHdrMode = SdrHdrMode.HDR;
            for (const monitorSetting of sessionParameters.monitorSettings) {
                monitorSetting.sdrHdrMode = SdrHdrMode.HDR;
            }
        }

        let srd: PM.SessionRequestData = {
            // Ideally all these values need not be sent to server, but our PM considers missing data as invalid.
            audioMode: 2,
            remoteControllersBitmap: sessionParameters.remoteControllersBitmap ?? 0,
            sdrHdrMode: sdrHdrMode,
            networkTestSessionId: null as unknown as string,
            availableSupportedControllers: [],
            clientVersion: CLIENT_VERSION,
            deviceHashId: this.deviceId,
            internalTitle: null as unknown as string,
            clientPlatformName: this.clientPlatformName, // long term plan is to get rid of this field
            metaData: [
                { key: "SubSessionId", value: subSessionId },
                {
                    key: "wssignaling",
                    value: SessionControlSettings.webSocketSignaling ? "1" : "0"
                }
            ],
            surroundAudioInfo: 0,
            clientTimezoneOffset:
                sessionParameters.clientTimeZoneOffset ??
                new Date().getTimezoneOffset() * 60 * 1000 * -1,
            clientIdentification: CLIENT_IDENTIFICATION,
            parentSessionId: null as unknown as string,
            appId: sessionParameters.appId as unknown as number,
            streamerVersion: 1 as unknown as string,
            clientRequestMonitorSettings: sessionParameters.monitorSettings,
            appLaunchMode: appLaunchMode,
            sdkVersion: "1.0",
            enhancedStreamMode: 1,
            useOps: true,
            clientDisplayHdrCapabilities: null as unknown as PM.ClientHdrCapabilities,
            accountLinked: sessionParameters.accountLinked ?? false,
            partnerCustomData: sessionParameters.partnerCustomData ?? "",
            enablePersistingInGameSettings:
                sessionParameters.enablePersistingInGameSettings ?? false,
            secureRTSPSupported: false,
            userAge: sessionParameters.userAge ?? 0,
            requestedStreamingFeatures: sessionParameters.streamingFeatures
        };
        if (sessionParameters.requestedAudioFormat) {
            srd.requestedAudioFormat = sessionParameters.requestedAudioFormat;
        }

        // todo: GS server is still relying on the "GSStreamerType", need to cleanup GS server code and then delete this.
        if (
            SessionControlSettings.webRtcStreamer ||
            this.clientPlatformName === BROWSER_PLATFORM_NAME
        ) {
            srd.metaData!.push({ key: "GSStreamerType", value: "WebRTC" });
        } else {
            // For non WebRTC client currently client must inform server of secureRTSP support. The "wssignaling" : "1" in metadata informs the server to be ready for websocket connection but without secureRTSPSupported, the connection doesnt work.
            // The above combination is broken, however we don't want any clients to perform non secure request to our server so hard code secureRTSPSupported to true.
            // WebRTC client hardcodes the Signaling port to 49100(secure) in GridServer. PM currently sends all the port for native client, this will be cleaned up in separate initiative soon.
            // if secureRTSPSupported is set to true in web client, PM will provide one more port to client which is not needed, hence restrict this to only non web client.
            srd.secureRTSPSupported = true;
        }

        for (const key in sessionParameters.metaData) {
            let x: PM.MetaData = {
                key: key,
                value: sessionParameters.metaData[key]
            };
            srd.metaData!.push(x);
        }

        let body: string;
        if (resume) {
            const sessionModifyObj: PM.SessionModify = {
                action: PM.Action.RESUME,
                data: "RESUME",
                sessionRequestData: srd,
                metaData: []
            };
            body = JSON.stringify(sessionModifyObj);
        } else {
            const sessionRequestObj: PM.SessionRequest = {
                sessionRequestData: srd
            };
            body = JSON.stringify(sessionRequestObj);
        }

        Log.i("{1daeef1}", "{c6c3ed4}", (resume ? "PUT" : "POST"), Log.sanitize(body));

        return body;
    }

    /**
     * Performs a single session related http request, handles the response, handles error cases and collects http telemetry.
     * Unless there is an exception in the catch block handling this function always resolves with ServerResponse object.
     * Developers should use this function instead of making direct http requests to server as this function generalizes the error handling, telemetry collection.
     * @param requestType - identifies the type of session request as response format from server can be different and helps decide if Http telemetry should be collected.
     * @param requestUrl - http url
     * @param body - http body to be sent to server.
     * @param abortController - passed to fetch API, caller can abort the fetch request by calling .abort on this object.
     * @param sessionId - optional sessionId, collected in telemetry.
     * @returns a promise which resolves with ServerResponse. @see ServerResponse for details.
     */
    protected performSessionRequest(
        requestType: SessionRequestType,
        requestUrl: string,
        body: string,
        abortController?: AbortController,
        sessionId?: string
    ): Promise<ServerResponse> {
        // Http analytics events are required in failure cases for any request.
        // If requests are cancelled then we should not send any telemetry event.
        // The GetSession request (Polling requests) doesn't need telemetry in success cases.
        const shouldSendAnalyticsEvent = (result: ScErrorCode): boolean => {
            if (isCancelCode(result)) {
                // don't send any event during intentional cancel.
                return false;
            } else if (result !== ScErrorCode.Success) {
                // always send on failure.
                return true;
            }

            // success cases, send for all but not for GetSessionInfo
            switch (requestType) {
                case SessionRequestType.GET_SESSIONINFO:
                    return false;
                default:
                    return true;
            }
        };

        const getHttpMethodForRequestType = (): string => {
            switch (requestType) {
                case SessionRequestType.CREATE_SESSION:
                    return "POST";
                case SessionRequestType.DELETE_SESSION:
                    return "DELETE";
                case SessionRequestType.RESUME_SESSION:
                case SessionRequestType.AD_UPDATE:
                    return "PUT";
                default:
                    return "GET";
            }
        };

        let options: RequestHttpOptions = {
            method: getHttpMethodForRequestType(),
            headers: this.httpRequestOptions.headers,
            body: body,
            retryCount: this.httpRequestOptions.retryCount,
            timeout: this.httpRequestOptions.timeout
        };

        // We have been setting keepalive only for delete request as this happens generally during the tab close.
        // setting keepalive will allow the request to outlive the page.
        // Ads Cancel request will also benefit from keepAlive = true.
        // There is not much use for any other requests to be kept alive, in fact not keeping the request alive for post/resume requests would be better.
        if (
            requestType === SessionRequestType.DELETE_SESSION ||
            requestType === SessionRequestType.AD_UPDATE
        ) {
            options.keepalive = true;
            if (
                this.platformDetails &&
                IsChromeBrowser(this.platformDetails) &&
                !IsChromeVersionAtLeast(this.platformDetails, 81)
            ) {
                // https://bugs.chromium.org/p/chromium/issues/detail?id=835821
                // Bug 3923439: Not supported in Chrome versions 80 and below
                options.keepalive = false;
            }
        }

        const startTime = performance.now();
        // Should we create telemetry event and send it in all failure cases ?
        let httpAnalyticsEvent: TelemetryHttpEvent | undefined =
            ScTelemetryHandler.getTelemetryHttpEvent(requestUrl, options.method ?? "", sessionId);
        httpAnalyticsEvent!.statusCode = "0";

        // keep this outside of promise constructor so that we can log data in exception.
        let responseData: string = "";

        return performHttpRequest(requestUrl, options, this.authInfo, abortController)
            .then((response: Response) => {
                // required for logging in catch handler.
                responseData = response.data;

                const endTime = performance.now();
                httpAnalyticsEvent!.statusCode = response.status.toString();
                httpAnalyticsEvent!.callDuration = Math.round(endTime - startTime);

                if (!response.data) {
                    // Ideally this block should not get executed.
                    Log.e("{1daeef1}", "{ea40865}");
                    throw new SyntaxError("The response from server does not contain any data");
                }

                let pmResponse: PmResponse;
                try {
                    pmResponse = JSON.parse(response.data);
                } catch (err) {
                    httpAnalyticsEvent!.response = response.data; // Emit response data as well to check what gets returned in case of parsing failure
                    throw err;
                }

                if (!pmResponse.requestStatus) {
                    Log.e("{1daeef1}", "{862f264}", Log.sanitize(
                            response.data
                        ));
                    throw new SyntaxError("RequestStatus missing in response");
                }

                const serverId = pmResponse.requestStatus.serverId.toUpperCase();

                httpAnalyticsEvent!.requestId = pmResponse.requestStatus.requestId;
                httpAnalyticsEvent!.serverId = serverId;
                httpAnalyticsEvent!.requestStatusCode = pmResponse.requestStatus.statusDescription!;

                const sessionResponse: ServerResponse = {
                    result: getResultFromRequestStatus(pmResponse.requestStatus),
                    pmResponse,
                    serverId
                };
                this.addRequestStatusToSpan(pmResponse.requestStatus);

                if (requestType === SessionRequestType.GET_ACTIVE_SESSIONS) {
                    const activeSessions = pmResponse.sessions;
                    if (activeSessions) {
                        sessionResponse.activeSessions = this.extractSessionList(activeSessions);
                    }
                } else if (requestType !== SessionRequestType.DELETE_SESSION) {
                    // PM doesn't provide any session response for Delete request.
                    // todo: we will miss setting sessionId in the http analytics event for delete session.
                    // extract from URL ? or accept as a parameter in this function.
                    const responseSession = pmResponse.session;
                    if (responseSession) {
                        sessionResponse.session = this.extractSessionInformation(responseSession);
                        this.addSessionInfoToSpan(responseSession);
                        // For POST request this just overrides the sessionId, rest all requests will have same sessionid.
                        httpAnalyticsEvent!.sessionId = sessionResponse.session.sessionId;
                    }

                    const otherSessions = pmResponse.otherUserSessions;
                    if (otherSessions) {
                        sessionResponse.activeSessions = this.extractSessionList(otherSessions);
                        if (sessionResponse.activeSessions.length) {
                            Log.d("{1daeef1}", "{ba4be1c}", sessionResponse.activeSessions.length);
                        }
                    }
                }

                if (httpAnalyticsEvent && shouldSendAnalyticsEvent(sessionResponse.result)) {
                    ScTelemetryHandler.sendHttpEvent(httpAnalyticsEvent);
                    httpAnalyticsEvent = undefined;
                }

                return sessionResponse;
            })
            .catch(err => {
                // Request did not complete, aborted/timeout/networkerror. or an exception from above then block.
                let sessionResponse: ServerResponse = {
                    result: ScErrorCode.NetworkError
                };

                const endTime = performance.now();
                if (err.code) {
                    // Failure of the httpRequest, covert the Utils code to sessionResponse.result.
                    sessionResponse.result = convertUtilsErrorCodeToScErrorCode(err.code);
                } else if (isParseError(err)) {
                    sessionResponse.result = ScErrorCode.ResponseParseFailure;
                    Log.e("{1daeef1}", "{04bcd6f}", Log.sanitize(
                            responseData
                        ));
                } else {
                    sessionResponse.result = ScErrorCode.DOMExceptionInSessionControl;
                    Log.e("{1daeef1}", "{94d55bb}", err, err.stack);
                    ScTelemetryHandler.sendExceptionEvent(
                        err,
                        "performSessionRequest",
                        LOGTAG + ".ts",
                        0,
                        0,
                        true
                    );
                }

                if (httpAnalyticsEvent && shouldSendAnalyticsEvent(sessionResponse.result)) {
                    httpAnalyticsEvent.callDuration = Math.round(endTime - startTime);
                    ScTelemetryHandler.sendHttpEvent(httpAnalyticsEvent);
                }

                Log.e("{1daeef1}", "{9c647aa}", options.method, requestUrl);
                Log.e("{1daeef1}", "{200d7ba}", sessionResponse.result, getErrorCodeDescription(
                        sessionResponse.result
                    ));
                return sessionResponse;
            });
    }

    /**
     * Provides the SessionUpdate interface populated using the session object provided from server.
     * Clients will use the returned object to display progress state/queue position/eta/ads to the user.
     * @see startOrResumeSession
     * @param pmSession - session object received from server.
     * @param lastQueuePosition - last queue position informed to client.
     *       In GeForce NOW server a session position in queue can move in either direction based on the requests from users with different subscriptions.
     *       If clients display progress bar based on queue position it might keeping going back and forth.
     *       This function prevents the queue position returned to client to be always <= the last provided queue position.
     * @param subSessionId - Id for the startSession/resumeSession instance.
     * @returns SessionUpdate
     */
    protected getSessionUpdate(
        pmSession: PM.Session,
        lastQueuePosition: number,
        subSessionId: string
    ): SessionUpdate {
        let _state = SessionProgressState.CONFIGURING;
        let queuePosition: number = 0;
        if (pmSession.seatSetupInfo) {
            switch (pmSession.seatSetupInfo.seatSetupStep) {
                case PM.SeatSetupStep.UNKNOWN:
                    _state = SessionProgressState.CONNECTING;
                    break;
                case PM.SeatSetupStep.WAITING_FOR_RESOURCE:
                    _state = SessionProgressState.IN_QUEUE;
                    if (pmSession.seatSetupInfo.queuePosition! < lastQueuePosition) {
                        queuePosition = pmSession.seatSetupInfo.queuePosition!;
                    } else {
                        queuePosition = lastQueuePosition;
                    }
                    break;
                case PM.SeatSetupStep.WAITING_FOR_PREVIOUS_SESSION_CLEANUP:
                    _state = SessionProgressState.PREVIOUS_SESSION_CLEANUP;
                    break;
                default:
                    queuePosition = 0;
            }
        }

        const sessionUpdate: SessionUpdate = {
            sessionId: pmSession.sessionId,
            subSessionId: subSessionId,
            queuePosition: queuePosition,
            eta: pmSession.seatSetupInfo?.seatSetupEta ?? 0,
            progressState: _state,
            isAdsRequired: pmSession.sessionAdsRequired,
            ads: []
        };

        const oldAdsStateInfo = new Map(this.adsStateInfo);
        this.adsStateInfo.clear();

        if (pmSession.sessionAds) {
            // Generally there will be one or two ads.
            const adsCount = pmSession.sessionAds.length;
            for (let i = 0; i < adsCount; i++) {
                const sessionAd = pmSession.sessionAds[i];

                // PM will remove all the finished/cancelled ads. If not, ignore them.
                // Client has no use of finished/cancelled Ads.
                if (
                    sessionAd.adState == AdState.FINISHED ||
                    sessionAd.adState == AdState.CANCELED
                ) {
                    continue;
                }

                const adId = sessionAd.adId;
                // We only accept ad updates for Ads still valid in server.
                // Retain the last known client state as all updates might not have reached server, hence serverAdState and clientAdState might not match.
                // In current implementation we are not using the serverAdState yet.
                this.adsStateInfo.set(adId, {
                    serverAdState: sessionAd.adState,
                    lastClientAdState:
                        oldAdsStateInfo.get(adId)?.lastClientAdState ?? AdState.UNKNOWN
                });

                // Below code will be replaced as part of schema to typescript interfaces generation.
                const ad: AdData = {
                    adId: adId,
                    adState: sessionAd.adState,
                    adUrl: sessionAd.adUrl,
                    adLengthInSeconds: sessionAd.adLengthInSeconds,
                    adMediaFiles: []
                };

                const adMediaFilesCount = sessionAd.adMediaFiles?.length ?? 0;
                for (let i = 0; i < adMediaFilesCount; i++) {
                    const sessionAdMediaFile = sessionAd.adMediaFiles[i];
                    const adMediaFile: SessionAdMediaFileData = {
                        mediaFileUrl: sessionAdMediaFile.mediaFileUrl,
                        encodingProfile: sessionAdMediaFile.encodingProfile
                    };
                    ad.adMediaFiles.push(adMediaFile);
                }
                sessionUpdate.ads.push(ad);
            }
        }
        return sessionUpdate;
    }

    /**
     * Returns the wait time between requests to server during session setup/resume process.
     * The interval is decided based on queue position/ads requirement.
     * @param pmSession - JSON session object received by server.
     * @returns the polling interval
     */
    protected getPollingInterval(pmSession: PM.Session) {
        let pollingInterval = this.pollingOptions.minInterval;
        if (
            pmSession.seatSetupInfo!.seatSetupStep === PM.SeatSetupStep.WAITING_FOR_RESOURCE &&
            this.pollingOptions.queueSizePerStep != 0
        ) {
            pollingInterval +=
                Math.floor(
                    pmSession.seatSetupInfo!.queuePosition! / this.pollingOptions.queueSizePerStep
                ) * this.pollingOptions.step;
        }

        // If PM informs us that session requires Ad then PM wants us to do a GET request only after at least 3 seconds.
        // So that PM can perform the /session /opportunity requests to PlayerWON service and get Ads.
        // This isn't a great user experience. This requirement might change eventually, for now honor what PM team has requested us to do.
        // For higher queue positions it might take time for PM to process ads for that session, retain the normal polling interval.
        // Note: once all Ads are provided by PM then this field is set to false and client will continue the normal polling interval.
        if (pmSession.sessionAdsRequired) {
            pollingInterval = Math.max(
                pollingInterval,
                this.pollingOptions.adsPollingIntervalMinMS
            );
        }

        return Math.min(pollingInterval, this.pollingOptions.maxInterval);
    }

    /**
     * Returns true if clients have configured the auth information through setAuthInfo.
     * @returns boolean
     */
    protected isAuthInfoAvailable(): boolean {
        return this.authInfo.type === AuthType.NONE || !!this.authInfo.token;
    }

    /**
     * Implements the session start/resume process.
     * This is called from both startSession and resumeSessionAPI with the parameters provided by client.
     * The returned promise is resolved eventually once the session is ready for connection or in error case.
     * @param sessionParameters
     * @param cancelController
     * @param updateCallback
     * @param sessionId - if not set will be treated as new session setup process else resumes this session.
     * @returns
     */
    private startOrResumeSession(
        sessionParameters: SessionParameters,
        cancelController: AbortController,
        updateCallback: SessionUpdateCallback,
        sessionId?: string
    ): Promise<SessionStartResumeResult> {
        // Every startSession/resumeSession needs a unique subSession Id for identification in telemetry.
        // do not set this in gsTelemetryHandler until initial validation.
        const subSessionId: string = getNewGuid();

        return new Promise<SessionStartResumeResult>((resolve, reject) => {
            const isResume = sessionId ? true : false;

            const invalidOperationHandler = () => {
                const invalidOperationResult: SessionStartResumeResult = {
                    code: ScErrorCode.InvalidOperation,
                    subSessionId: subSessionId
                };
                this.addResultToSpan(invalidOperationResult.code);

                // Should not happen in production, collect error just in case.
                const launchTelemetryEvent: GridServer_GameLaunch_Request =
                    ScTelemetryHandler.getGameLaunchRequestEvent(
                        sessionParameters.serverAddress ?? "",
                        isResume,
                        sessionId
                    );
                launchTelemetryEvent.result = GetHexString(invalidOperationResult.code);
                launchTelemetryEvent.subSessionId = subSessionId;
                ScTelemetryHandler.sendGameLaunchRequestEvent(launchTelemetryEvent);

                resolve(invalidOperationResult);
            };

            // Both start and resume sessions needs authentication.
            if (!this.isAuthInfoAvailable()) {
                Log.e("{1daeef1}", "{3781843}");
                invalidOperationHandler();
                return;
            }

            // We only support one start/resume at a time, no technical limitation, no use case needs it.
            // if clients are attempting to start/resume again while one in progress then probably client did not invoke abort on the previous abortController.
            if (this.isSessionSetupInProgress()) {
                Log.e("{1daeef1}", "{3753849}");
                invalidOperationHandler();
                return;
            }

            // todo: evaluate if we can avoid caching CMSID in gsTelemetryHandler
            ScTelemetryHandler.setCmsId(sessionParameters.appId);
            ScTelemetryHandler.setSubSessionId(subSessionId);

            const url: string = this.createSessionRequestURL(sessionParameters, sessionId);

            const server = new URL(url).hostname;
            const launchTelemetryEvent: GridServer_GameLaunch_Request =
                ScTelemetryHandler.getGameLaunchRequestEvent(server, isResume);
            this.sessionSetupData = {
                resolve: resolve,
                reject: reject,
                sessionId: sessionId,
                subSessionId: subSessionId,
                sessionUpdateCallback: updateCallback,
                lastQueuePosition: Number.MAX_VALUE,
                isSessionInQueue: false,
                cancelController: cancelController,
                launchTelemetryEvent: launchTelemetryEvent,
                startTime: performance.now()
            };

            const body: string = this.getSessionRequestData(
                sessionParameters,
                isResume,
                subSessionId
            );
            this.performSessionRequest(
                isResume ? SessionRequestType.RESUME_SESSION : SessionRequestType.CREATE_SESSION,
                url,
                body,
                cancelController
            )
                .then((response: ServerResponse) => {
                    if (!isResume && response.result === ScErrorCode.Success && response.session) {
                        Log.i("{1daeef1}", "{7c01bce}", response.session.sessionId);
                    }
                    this.processSessionSetupServerResponse(response);
                })
                .catch((err: any) => {
                    this.processSessionSetupException(err);
                });
        }).catch((err: any) => {
            // We capture most of the exception, collect telemetry, launch event etc.
            // this should be an unexpected case, adding to telemetry just in case.
            this.adsStateInfo.clear();
            this.adUpdateArray.clear();
            this.sessionSetupData = undefined;

            this.processException(err, "startOrResumeSession");
            this.addResultToSpan(ScErrorCode.DOMExceptionInSessionControl);

            const result: SessionStartResumeResult = {
                code: ScErrorCode.DOMExceptionInSessionControl,
                subSessionId: subSessionId
            };
            return result;
        });
    }

    /**
     * Part of session setup process.
     * Initiates the GET session request during session setup/resume polling.
     * This further results in processing of next action during polling asynchronously.
     * @returns void
     */
    protected performSessionSetupPollingRequest(): void {
        if (!this.sessionSetupData) {
            return;
        }

        const sessionSetupData = this.sessionSetupData;
        const url = this.getUrlForSession(sessionSetupData.sessionId ?? "");
        this.performSessionRequest(
            SessionRequestType.GET_SESSIONINFO,
            url,
            "",
            this.sessionSetupData.cancelController,
            this.sessionSetupData.sessionId ?? ""
        )
            .then((response: ServerResponse) => {
                this.processSessionSetupServerResponse(response);
            })
            .catch((err: any) => {
                this.processSessionSetupException(err);
            });
    }

    /**
     * Part of session setup process.
     * Emits exception and resolves the sessions setup process.
     * @param err - Exception caught in one of the session setup process functions.
     */
    private processSessionSetupException(err: any) {
        Log.e("{1daeef1}", "{e3be7ea}", err.message);
        ScTelemetryHandler.sendExceptionEvent(
            err,
            "processSessionSetupException",
            LOGTAG + ".ts",
            0,
            0,
            true
        );
        this.resolveStartResumeSession(ScErrorCode.DOMExceptionInSessionControl);
    }

    /**
     * Resolves the promise returned in startSession/resumeSession.
     * This is also responsible for emitting the launch telemetry event and clearing session setup related variables.
     * @param code - Result to be returned to client.
     * @param zoneName - zone which provided the response. undefined in network error/exception cases.
     * @param activeSessions - active sessions provided by server.
     * @returns void
     */
    private resolveStartResumeSession(
        code: ScErrorCode,
        zoneName?: string,
        activeSessions?: Session[]
    ): void {
        // avoids the need for null check in rest of this function.
        if (!this.sessionSetupData) {
            return;
        }

        // Any exceptions in any of the session setup process leads to this function.
        // However if this function throws an exception, it will result in unhandled exception.
        // catch the exception and do a promise rejection.
        try {
            const sessionStartResumeResult: SessionStartResumeResult = {
                code: code,
                subSessionId: this.sessionSetupData.subSessionId ?? "",
                session: this.sessionSetupData.lastSessionFromServer,
                zoneName: zoneName,
                activeSessions: activeSessions
            };

            const launchTelemetryEvent = this.sessionSetupData.launchTelemetryEvent;
            launchTelemetryEvent.result = GetHexString(code);
            launchTelemetryEvent.zoneName = zoneName ?? "";
            launchTelemetryEvent.sessionId = this.sessionSetupData.sessionId ?? "";
            // note: adding calculated duration to existing duration as GridServer usage already has the duration for put/post request in this event.
            launchTelemetryEvent.launchDuration +=
                performance.now() - this.sessionSetupData.startTime;
            ScTelemetryHandler.sendGameLaunchRequestEvent(
                this.sessionSetupData.launchTelemetryEvent
            );

            this.addResultToSpan(code);

            Log.d("{1daeef1}", "{c4902fa}", GetHexString(code), 
                    this.sessionSetupData.sessionId ?? ""
                );

            this.sessionSetupData.resolve(sessionStartResumeResult);
            ScTelemetryHandler.setCmsId("");
            ScTelemetryHandler.setSubSessionId("");
            this.sessionSetupData = undefined;
            this.adsStateInfo.clear();
            this.adUpdateArray.clear();
        } catch (err: any) {
            if (this.sessionSetupData) {
                this.sessionSetupData.reject(err);
                this.sessionSetupData = undefined;
            }
        }
    }

    /**
     * Implements the logic for next action after response from server during session setup process.
     * @param response - return value of performSessionRequest function.
     * @returns
     */
    private processSessionSetupServerResponse(response: ServerResponse): void {
        // sessionSetupData will always be set when code flow reaches here.
        // adding the below check to avoid checks in rest of the function.
        if (!this.sessionSetupData) {
            return;
        }

        const cancelHandler = () => {
            this.resolveStartResumeSession(
                sessionSetupData.isSessionInQueue
                    ? ScErrorCode.SessionSetupCancelledDuringQueuing
                    : ScErrorCode.SessionSetupCancelled
            );
        };

        const sessionSetupData = this.sessionSetupData;
        if (response.session) {
            response.session.subSessionId = this.sessionSetupData.subSessionId;
            this.sessionSetupData.lastSessionFromServer = response.session;
            this.sessionSetupData.sessionId = response.session.sessionId;
            // keep track of session setup state so that cancel code can be set appropriately.
            sessionSetupData.isSessionInQueue =
                response.pmResponse!.session!.seatSetupInfo!.seatSetupStep ==
                PM.SeatSetupStep.WAITING_FOR_RESOURCE;
        }

        if (response.result === ScErrorCode.Success && response.session) {
            const session = response.session;
            if (
                session.state === SessionState.READY_FOR_CONNECTION ||
                session.state === SessionState.STREAMING
            ) {
                // hack: GS server is sending fake notifications to NGS about stream connected.
                // we are facing race case while getSession, until thats fixed, consider
                // playing state as ready for connection.
                // todo: debug https://gfn.data.nvidiagrid.net/goto/f1580910-94a4-11ee-92ac-27f91702ba9c?sg_tenant=Global
                if (session.state == SessionState.STREAMING) {
                    ScTelemetryHandler.sendDebugEvent("InvalidSessionStateTransition", "Streaming");
                }

                // Successfully established session.
                Log.i("{1daeef1}", "{30d6f44}", this.sessionSetupData.sessionId ?? "");
                this.resolveStartResumeSession(ScErrorCode.Success, response.serverId);
            } else if (
                session.state == SessionState.INITIALIZING ||
                session.state == SessionState.RESUMING
            ) {
                const sessionUpdate: SessionUpdate = this.getSessionUpdate(
                    response.pmResponse!.session!,
                    this.sessionSetupData.lastQueuePosition,
                    this.sessionSetupData.subSessionId
                );
                this.sessionSetupData.sessionUpdateCallback(sessionUpdate);

                if (sessionSetupData.isSessionInQueue) {
                    this.sessionSetupData.lastQueuePosition = sessionUpdate.queuePosition;
                }

                // If we are providing ad info to client then switch to processing of AdUpdates from clients.
                if (sessionUpdate.ads.length) {
                    this.processAdUpdates(
                        sessionSetupData.sessionId ?? "",
                        sessionSetupData.cancelController,
                        this.processSessionSetupServerResponse.bind(this),
                        this.processSessionSetupException.bind(this),
                        cancelHandler,
                        this.performSessionSetupPollingRequest.bind(this)
                    );
                } else {
                    // No ads to be played or all ad play is completed.
                    // wait for polling interval and attempt next GET session request or till clients initiates abort.
                    const wait = new CancellableWait(
                        this.getPollingInterval(response.pmResponse!.session!),
                        this.sessionSetupData.cancelController?.signal
                    );
                    wait.getPromise()
                        .then(this.performSessionSetupPollingRequest.bind(this))
                        .catch(cancelHandler);
                }
            } else {
                Log.e("{1daeef1}", "{ce47cd1}", Log.sanitize(JSON.stringify(response.pmResponse)));
                this.resolveStartResumeSession(ScErrorCode.SessionFinishedState, response.serverId);
            }
        } else if (response.result === ScErrorCode.RequestCancelled) {
            cancelHandler();
        } else {
            // error case
            this.resolveStartResumeSession(
                response.result,
                response.serverId,
                response.activeSessions
            );
        }
    }

    /**
     * Collects exception telemetry event and logs exception.
     * @param error - argument passed to catch handler.
     * @param sourceFunc - function where exception was caught.
     */
    private processException(error: any, sourceFunc: string) {
        Log.e("{1daeef1}", "{32f1296}", sourceFunc, error.message);
        Log.e("{1daeef1}", "{94d55bb}", error, error.stack);
        ScTelemetryHandler.sendExceptionEvent(error, sourceFunc, LOGTAG + ".ts", 0, 0, true);
    }
}
